import { Box } from "../../components/ui/box";
import { BOX_GROUP } from "../../components/ui/box-classes";
import { Button } from "../../components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { Icon } from "../../components/ui/icon";
import { Menu, MenuPopup, MenuTrigger } from "../../components/ui/menu";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
} from "../../components/ui/sidebar";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import type { IconName } from "../../components/ui/icon-registry";
import type React from "react";
import type { Omittable } from "../../lib/props";

export type SidebarSectionProps = Omittable<{
  /** Group heading; doubles as the collapse trigger. */
  title: string;
  defaultOpen?: boolean;
  /**
   * `compact` tightens the section's vertical block padding for sidebars that
   * stack sections with no separators between them, where the roomy default
   * reads as a hole. Omit for the default rhythm (separator-delimited
   * sections).
   */
  spacing?: "compact";
  /**
   * Optional header affordance (e.g. a "+" create action). Revealed on hover
   * beside the heading (with the overflow menu, if any). `label` is both the
   * accessible name and the hover tooltip.
   */
  action?: { icon: IconName; label: string; onClick: () => void };
  /**
   * Optional overflow menu — the `MenuPopup` content (its `MenuItem`s). When
   * set, a "···" trigger reveals on hover to the left of the action.
   */
  menu?: React.ReactNode;
  /** `SidebarMenuItem` rows. The section supplies the enclosing menu list. */
  children: React.ReactNode;
}>;

/**
 * A collapsible titled group of sidebar rows: the base `SidebarGroup` parts
 * driven by a `Collapsible`, with the heading as the trigger.
 *
 * At rest the header is just its muted heading. On hover the row washes and its
 * chrome fades in — the caret (indicating it collapses) and the overflow/action
 * buttons — the reference section-header idiom.
 *
 * Domain-free composition — it knows nothing about the rows it titles, so any
 * sidebar can group with it.
 */
export function SidebarSection({
  title,
  defaultOpen = true,
  action,
  menu,
  spacing,
  children,
}: SidebarSectionProps) {
  return (
    <Collapsible
      defaultOpen={defaultOpen}
      // The reveal scope for the header chrome below. Taken from the one
      // `BOX_GROUP` list so the pair cannot drift into a silent no-op.
      // `py-2.5` gives a touch more breathing room between sections than the
      // base group so the rhythm reads generous (the design reference);
      // `compact` (`py-1`) is for sidebars that stack sections with no
      // separators, where doubled 10px gutters read as a hole.
      render={
        <SidebarGroup
          className={`${BOX_GROUP.section} ${spacing === "compact" ? "py-1" : "py-2.5"}`}
        />
      }
    >
      {/* The 24px trigger sits in a 30px block (4px above, 2px below — the
          reference-measured asymmetry): the heading leans toward the rows it
          titles rather than floating equidistant between sections. That
          padding lives HERE, outside the positioning context below, so it
          cannot pull the header chrome off the title: an overlay spanning the
          padded block centers on the block, which the asymmetry has already
          moved off the heading's own center. */}
      <Box
        data-slot="sidebar-section-heading"
        paddingTop="xs"
        paddingBottom="2xs"
      >
        {/* Positioning context for the header chrome — the heading row and
            nothing else, so `top:0/bottom:0` IS the title's extent and the
            actions center on it exactly. */}
        <Box position="relative">
          <SidebarGroupLabel
            // 24px compact heading that doubles as the collapse trigger and
            // washes on hover. `w-full` so the wash spans the whole pane column
            // (like the rows) instead of hugging the title. The caret points
            // right at rest and rotates to down when open (`data-panel-open`):
            // expanded `▾`, collapsed `▸` — the reference tree-toggle idiom.
            className="h-6 w-full cursor-pointer hover:bg-wash-hover data-panel-open:*:data-[slot=icon]:rotate-90"
            render={<CollapsibleTrigger />}
          >
            {title}
            {/* Registry caret trailing the title, hidden until the section is
                hovered (or focus lands on it); a coarse pointer has no hover,
                so the caret stands there — it is the only cue that the
                heading collapses. */}
            <Icon
              className="ms-1 opacity-0 transition-[opacity,transform] group-hover/section:opacity-100 group-focus-visible/section:opacity-100 pointer-coarse:opacity-100"
              name="caret-right"
              size="sm"
            />
          </SidebarGroupLabel>
          {(action != null || menu != null) && (
            // Header chrome, overlaying the heading row's right edge and
            // centered on its height. A sibling of the trigger — never nested inside
            // it — so pressing a button never toggles the collapse. Hover-revealed
            // on pointer devices, always shown on touch (where hover never fires).
            // The right offset puts the trailing button's glyph center on the
            // same vertical line as the rows' `SidebarMenuAction` (a 20px button
            // at right-1 → center 14px from the edge): the icon-xs button is
            // 24px from `sm` up (14 − 12 = 2) and 28px below it (14 − 14 = 0).
            <Box
              position="absolute"
              top={0}
              bottom={0}
              right={{ base: 0, sm: 2 }}
              direction="row"
              align="center"
              gap="2xs"
              hoverReveal="section"
              revealOnTouch
            >
              {menu != null && (
                <Menu>
                  <MenuTrigger
                    render={
                      <Button
                        aria-label={`${title} options`}
                        size="icon-xs"
                        variant="ghost"
                      />
                    }
                  >
                    <Icon name="ellipsis" size="sm" />
                  </MenuTrigger>
                  <MenuPopup align="end" side="bottom">
                    {menu}
                  </MenuPopup>
                </Menu>
              )}
              {action != null && (
                // The label is the whole contract: it names the action in the
                // a11y tree and on hover. A lone glyph revealed by hover has no
                // other way to say what it does.
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        aria-label={action.label}
                        onClick={action.onClick}
                        size="icon-xs"
                        variant="ghost"
                      />
                    }
                  >
                    <Icon name={action.icon} size="sm" />
                  </TooltipTrigger>
                  <TooltipPopup side="top">{action.label}</TooltipPopup>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <CollapsibleContent render={<SidebarGroupContent />}>
        <SidebarMenu>{children}</SidebarMenu>
      </CollapsibleContent>
    </Collapsible>
  );
}
